import { useRef, useState } from 'react';

import { PHOTO_UPLOAD_MAX_DIMENSION, PHOTO_UPLOAD_QUALITY, PhotoFormElementAnswerSchema, PhotoFormElement as PhotoFormElementType } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import Compressor from 'compressorjs';
import { z } from 'zod';

import CameraIcon from '@/components/icons/camera';
import DeleteIcon from '@/components/icons/delete';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lightbox } from '@/components/ui/lightbox';
import { useToast } from '@/components/ui/toaster';
import { convertFullViewImageUrl, convertImageUrl } from '@/utils/image-url';

import { ConfigLabel } from '../config-label';
import { FormFieldError } from '../form-field-error';

// Maximum number of photos allowed
const MAX_PHOTOS = 10;

export const PhotoFormElement = ({
  element,
  value,
  onChange,
  onBlur,
  readonly,
  isPreview = false,
  error,
}: {
  element: PhotoFormElementType;
  value?: z.infer<typeof PhotoFormElementAnswerSchema>['value'];
  onChange: (value: z.infer<typeof PhotoFormElementAnswerSchema>['value']) => void;
  onBlur?: () => void;
  readonly?: boolean;
  isPreview?: boolean;
  error?: string;
}) => {
  const [lightboxStartIndex, setLightboxStartIndex] = useState<number | undefined>(undefined);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files?.length) return;

    const currentCount = value?.length || 0;
    const remainingSlots = MAX_PHOTOS - currentCount;

    if (remainingSlots <= 0) return;

    // Only process up to the remaining slots
    const filesToProcess = Array.from(files).slice(0, remainingSlots);

    const newImages: string[] = [];
    for (const file of filesToProcess) {
      try {
        // Compress and convert to base64
        const base64 = await new Promise<string>((resolve, reject) => {
          new Compressor(file, {
            quality: PHOTO_UPLOAD_QUALITY,
            maxWidth: PHOTO_UPLOAD_MAX_DIMENSION,
            maxHeight: PHOTO_UPLOAD_MAX_DIMENSION,
            convertSize: 200000,
            success: (compressedFile) => {
              const reader = new FileReader();
              reader.onload = (e) => resolve(e.target?.result as string);
              reader.onerror = (e) => reject(e);
              reader.readAsDataURL(compressedFile);
            },
            error: (err) => reject(err),
          });
        });
        newImages.push(base64);
      } catch (error) {
        console.error('Image processing failed:', error);
        toast.error('That image could not be added. Please try a different one.');
      }
    }

    onChange([...(value ?? []), ...newImages]);
    onBlur?.();
    // Reset input to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemove = (index: number) => {
    const newImages = (value ?? []).filter((_, i) => i !== index);
    onChange(newImages);
  };

  const currentCount = value?.length || 0;
  const isLimitReached = currentCount >= MAX_PHOTOS;

  return (
    <>
      <div className="space-y-1">
        <div className="space-y-2">
          <Label required={element.required}>{element.label}</Label>

          <div className="flex flex-wrap gap-4">
            {(value ?? []).map((image, index) => {
              // Check if image is base64 (data URL) or S3 URL
              const isBase64 = image.startsWith('data:');
              const imageSrc = isBase64 ? image : convertImageUrl(image, { width: 96, height: 96 });
              return (
                <div key={index} className="relative h-24 w-24">
                  <img
                    src={imageSrc}
                    alt={`${element.label} ${index + 1}`}
                    className="h-full w-full cursor-pointer rounded-md object-contain"
                    onClick={() => setLightboxStartIndex(index)}
                  />
                  {!readonly && (
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      aria-label="Remove photo"
                      className="absolute -top-2 -right-2 size-4 rounded-full"
                      onClick={() => handleRemove(index)}>
                      <DeleteIcon className="size-3" />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handleFileChange}
              onBlur={onBlur}
              id={`file-input-${element.id}`}
              disabled={readonly || isPreview || isLimitReached}
            />

            {(!readonly || isPreview) && (
              <>
                <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={isPreview || isLimitReached}>
                  <CameraIcon className="mr-2 h-4 w-4" />
                  Add {currentCount > 0 ? 'More ' : ''}Photos
                </Button>
                {currentCount > 0 && (
                  <span className="text-muted-foreground ml-2 text-sm">
                    {currentCount}/{MAX_PHOTOS} photos
                  </span>
                )}
                {isLimitReached && <p className="text-muted-foreground mt-1 text-sm">Maximum number of photos reached (10)</p>}
              </>
            )}
          </div>
        </div>

        <FormFieldError error={error} />
      </div>
      {lightboxStartIndex !== undefined && (
        <Lightbox
          photos={(value ?? []).map((photo) => (photo.startsWith('data:') ? photo : convertFullViewImageUrl(photo)))}
          onHide={() => setLightboxStartIndex(undefined)}
          startIndex={lightboxStartIndex}
        />
      )}
    </>
  );
};

export function PhotoFormElementMock({ element }: { element: PhotoFormElementType }) {
  const [value, setValue] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files?.length) return;

    const remainingSlots = MAX_PHOTOS - value.length;

    if (remainingSlots <= 0) return;

    // Only process up to the remaining slots
    const filesToProcess = Array.from(files).slice(0, remainingSlots);

    const newImages: string[] = [];
    for (const file of filesToProcess) {
      try {
        // Compress and convert to base64
        const base64 = await new Promise<string>((resolve, reject) => {
          new Compressor(file, {
            quality: PHOTO_UPLOAD_QUALITY,
            maxWidth: PHOTO_UPLOAD_MAX_DIMENSION,
            maxHeight: PHOTO_UPLOAD_MAX_DIMENSION,
            convertSize: 200000,
            success: (compressedFile) => {
              const reader = new FileReader();
              reader.onload = (e) => resolve(e.target?.result as string);
              reader.onerror = (e) => reject(e);
              reader.readAsDataURL(compressedFile);
            },
            error: (err) => reject(err),
          });
        });
        newImages.push(base64);
      } catch (error) {
        console.error('Image processing failed:', error);
        toast.error('That image could not be added. Please try a different one.');
      }
    }

    setValue([...value, ...newImages]);
    // Reset input to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemove = (index: number) => {
    const newImages = value.filter((_, i) => i !== index);
    setValue(newImages);
  };

  const isLimitReached = value.length >= MAX_PHOTOS;

  return (
    <div>
      <div className="flex flex-wrap gap-4">
        {value.map((image, index) => (
          <div key={index} className="relative h-24 w-24">
            <img src={image} alt={`${element.label} ${index + 1}`} className="h-full w-full rounded-md object-contain" />
            <Button
              type="button"
              variant="destructive"
              size="icon"
              aria-label="Remove photo"
              className="absolute -top-2 -right-2 size-4 rounded-full"
              onClick={() => handleRemove(index)}>
              <DeleteIcon className="size-3" />
            </Button>
          </div>
        ))}
      </div>

      <div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFileChange}
          id={`file-input-${element.id}`}
          disabled={isLimitReached}
        />

        <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled>
          <CameraIcon className="mr-2 h-4 w-4" />
          Add {value.length > 0 ? 'More ' : ''}Photos
        </Button>
        {value.length > 0 && (
          <span className="text-muted-foreground ml-2 text-sm">
            {value.length}/{MAX_PHOTOS} photos
          </span>
        )}
        {isLimitReached && <p className="text-muted-foreground mt-1 text-sm">Maximum number of photos reached (10)</p>}
      </div>
    </div>
  );
}

export function PhotoFormElementConfig({ element, onChange }: { element: PhotoFormElementType; onChange?: (updates: Partial<PhotoFormElementType>) => void }) {
  return (
    <div className="flex flex-col gap-4">
      <ConfigLabel>
        <span>Label:</span>
        <Input value={element.label} onChange={(e) => onChange?.({ label: e.target.value })} />
      </ConfigLabel>

      <ConfigLabel className="flex-row items-start gap-2">
        <Checkbox checked={element.required} onCheckedChange={(checked) => onChange?.({ required: !!checked })} />
        <span>Required</span>
      </ConfigLabel>
    </div>
  );
}
